import type { AssetApiRecord } from './assets.types';
import { assetLabelIds } from './labels.queries';

interface LabelledAsset {
    primaryKey: string;
    labelIds: string[];
}

interface LabelChange {
    primaryKey: string;
    labelId: string;
    action: 'assign' | 'remove';
}

const toLabelledAssets = (records: AssetApiRecord[]): LabelledAsset[] => records.flatMap((record) => (
    record.primaryKey__ ? [{ primaryKey: record.primaryKey__, labelIds: assetLabelIds(record.tags__) }] : []
));

/** Labels every asset carries, and labels only some of them carry (shown as indeterminate). */
const summarizeLabels = (assets: LabelledAsset[]) => {
    const counts = new Map<string, number>();
    assets.forEach(({ labelIds }) => labelIds.forEach((id) => counts.set(id, (counts.get(id) ?? 0) + 1)));
    const entries = [...counts.entries()];
    return {
        assignedIds: entries.filter(([, count]) => count === assets.length).map(([id]) => id),
        partialIds: entries.filter(([, count]) => count < assets.length).map(([id]) => id),
    };
};

/** One request per asset that actually changes; assets already in the target state are skipped. */
const planLabelChanges = (assets: LabelledAsset[], added: string[], removed: string[]): LabelChange[] =>
    assets.flatMap(({ primaryKey, labelIds }) => [
        ...added.filter((id) => !labelIds.includes(id)).map((labelId) => ({ primaryKey, labelId, action: 'assign' as const })),
        ...removed.filter((id) => labelIds.includes(id)).map((labelId) => ({ primaryKey, labelId, action: 'remove' as const })),
    ]);

/** The assets' label state after the given changes went through, so a retry re-plans only what failed. */
const applyLabelChanges = (assets: LabelledAsset[], changes: LabelChange[]): LabelledAsset[] => assets.map((asset) => {
    const own = changes.filter((change) => change.primaryKey === asset.primaryKey);
    const removed = own.filter((change) => change.action === 'remove').map((change) => change.labelId);
    const assigned = own.filter((change) => change.action === 'assign').map((change) => change.labelId);
    return { ...asset, labelIds: [...asset.labelIds.filter((id) => !removed.includes(id)), ...assigned] };
});

/**
 * Label changes on one asset are a read-modify-write of its tags on the server, so they run sequentially per
 * asset; different assets run in parallel. Results keep the order of `changes`.
 */
const settleChangesPerAsset = async (
    changes: LabelChange[],
    apply: (change: LabelChange) => Promise<unknown>,
): Promise<PromiseSettledResult<unknown>[]> => {
    const results: PromiseSettledResult<unknown>[] = new Array(changes.length);
    const indicesByAsset = new Map<string, number[]>();
    changes.forEach((change, index) => {
        indicesByAsset.set(change.primaryKey, [...(indicesByAsset.get(change.primaryKey) ?? []), index]);
    });
    await Promise.all([...indicesByAsset.values()].map(async (indices) => {
        for (const index of indices) {
            try {
                results[index] = { status: 'fulfilled', value: await apply(changes[index]) };
            } catch (reason) {
                results[index] = { status: 'rejected', reason };
            }
        }
    }));
    return results;
};

export { applyLabelChanges, planLabelChanges, settleChangesPerAsset, summarizeLabels, toLabelledAssets };
export type { LabelChange, LabelledAsset };
